import { ProductFaq } from "../types";

const img = (n: string) => `/images/faq/v2/${n}.png`;

export const protectV2NL: ProductFaq = {
	title: "Veelgestelde vragen en probleemoplossing",
	downloads: [
		{ title: "FAQ en probleemoplossing (Nederlands)", path: "/documents/downloads/2026-10-21_Protect_FAQ-Troubleshooting-NL.pdf" },
		{ title: "Snelstartgids", path: "/documents/downloads/2026-06-16_Protect_QuickStartGuide_007570-R2A.pdf" },
		{ title: "Referentiegids", path: "/documents/downloads/2026-07-06_Protect_ReferenceGuide-ReferentieGids_007562-R2A.pdf" },
	],
	categories: [
		{
			id: "pairing",
			title: "Koppelen & verbinding",
			questions: [
				{
					id: "pair-first",
					title: "Hoe koppel ik mijn EarsOnly Protect voor de eerste keer?",
					blocks: [
						{
							t: "ol",
							items: [
								"Installeer de myProtect-app via de QR-code in je snelstartgids (of via [deze link](https://play.google.com/store/apps/details?id=nl.earsonly.myprotect)).",
								"Volg de instructies in de app. De Earbuds worden automatisch aan je apparaat gekoppeld.",
							],
						},
						{ t: "img", src: img("pairing-qr"), alt: "Scan de QR-code en open de myProtect-app" },
					],
				},
				{
					id: "pair-new",
					title: "Hoe koppel ik mijn EarsOnly Protect aan een nieuw apparaat?",
					blocks: [
						{
							t: "ol",
							items: [
								"Zorg dat de Oplaadcase minstens 10% is opgeladen (zie {{q:battery-check}}).",
								"Zorg dat de Earbuds goed in de Oplaadcase liggen.",
								"Houd de knop op de Oplaadcase minstens 3 seconden ingedrukt. Wacht tot de leds van beide Earbuds blauw worden; dit kan tot 5 seconden duren.",
								"Open de bluetooth-instellingen op je apparaat en kies de optie om een nieuw apparaat te verbinden of te koppelen.",
								"Selecteer “EarsOnly Protect” in de lijst om te koppelen en verbinding te maken.",
							],
						},
						{ t: "img", src: img("pairing-device"), alt: "De Earbuds koppelen via de bluetooth-instellingen van je apparaat" },
					],
				},
				{
					id: "pair-fail",
					title: "Waarom kan ik mijn Earbuds niet aan een apparaat koppelen?",
					blocks: [
						{
							t: "ol",
							items: [
								"Zorg dat de Oplaadcase minstens 10% is opgeladen (zie {{q:battery-check}}).",
								"Zorg dat beide Earbuds goed in de Oplaadcase liggen.",
								{
									text: "Druk drie seconden op de knop van de Oplaadcase en kijk naar de leds:",
									sub: [
										"Als de leds van de Oplaadcase en van beide Earbuds oplichten: beide Earbuds zijn verbonden met de Oplaadcase en kunnen nu gekoppeld worden. (Zie: {{q:pair-new}})",
										"Als de led van de Oplaadcase oplicht, maar die van een of beide Earbuds niet: een of beide Earbuds zijn niet verbonden met de Oplaadcase. Zie de volgende stappen.",
									],
								},
							],
						},
						{ t: "h", text: "Als alleen de led van de Oplaadcase oplicht" },
						{
							t: "ol",
							items: [
								"Haal beide Earbuds uit de Oplaadcase.",
								"Wacht 5 seconden.",
								"Leg beide Earbuds terug in de Oplaadcase.",
								"Controleer of de led van elk Earbud oplicht wanneer je het in de case legt.",
								"Druk opnieuw minstens 3 seconden op de knop tot de leds van de Earbuds oplichten.",
								"Lichten de leds van beide Earbuds nog steeds niet op? Reset dan beide Earbuds en de Oplaadcase en begin opnieuw bij stap 1. (Zie: {{q:reset-earbuds}} en {{q:reset-case}})",
								"Lichten de leds van beide Earbuds nu wel op? Koppel de Earbuds dan aan je apparaat. (Zie: {{q:pair-new}})",
							],
						},
						{ t: "h", text: "Lukt het koppelen nog steeds niet?" },
						{ t: "p", text: "Als de leds van beide Earbuds nog steeds niet oplichten, maken een of beide Earbuds mogelijk geen goed contact met de Oplaadcase." },
						{
							t: "ol",
							items: [
								"Maak de oplaadcontacten van de Earbuds en de Oplaadcase schoon met een licht vochtige doek. (Wacht tot de oplaadcontacten helemaal droog zijn voordat je de Earbuds terug in de Oplaadcase legt.)",
								"Druk opnieuw minstens 3 seconden op de knop tot de leds van de Earbuds oplichten.",
								"Lichten de leds van beide Earbuds en de led van de Oplaadcase op? Probeer dan opnieuw te koppelen.",
							],
						},
						{ t: "p", text: "Blijft het probleem bestaan? Reset dan beide Earbuds en de Oplaadcase en probeer opnieuw te koppelen. (Zie: {{q:reset-earbuds}} en {{q:reset-case}})" },
					],
				},
				{
					id: "connect-fail",
					title: "Waarom maken de Earbuds geen verbinding met mijn apparaat?",
					blocks: [
						{
							t: "ol",
							items: [
								"Leg de Earbuds terug in de Oplaadcase, wacht 10 seconden en haal ze er weer uit.",
								"Controleer in het bluetooth-menu van je apparaat (onder instellingen) of de Earbuds gekoppeld zijn. Zijn de Earbuds nog niet gekoppeld? Koppel ze dan aan je apparaat en volg de koppelinstructies. (Zie: {{q:pair-new}})",
								"Werkt dit niet? Reset dan de Earbuds (zie: {{q:reset-earbuds}}) en probeer het opnieuw.",
								"Start het apparaat waarmee je verbinding probeert te maken opnieuw op en probeer het opnieuw.",
								"Werkt dit niet? Verwijder de Earbuds dan uit de lijst met gekoppelde bluetooth-apparaten op je apparaat en koppel ze opnieuw (zie: {{q:pair-new}}).",
							],
						},
					],
				},
				{
					id: "disconnecting",
					title: "Waarom verliezen mijn Earbuds steeds de verbinding?",
					blocks: [
						{
							t: "ol",
							items: [
								"Controleer in het bluetooth-menu van je apparaat of de Earbuds nog verbonden zijn.",
								"Probeer de Earbuds te resetten door de knoppen van beide Earbuds tegelijk 15 seconden ingedrukt te houden.",
							],
						},
						{ t: "note", kind: "note", text: "Je voorkeursinstellingen gaan niet verloren." },
						{ t: "h", text: "Verliezen de Earbuds nog steeds de verbinding?" },
						{ t: "p", text: "Andere apparaten (zoals smartwatches) kunnen de verbinding storen. Bluetooth uitzetten op andere apparaten in de buurt kan dit oplossen." },
					],
				},
			],
		},
		{
			id: "use",
			title: "Gebruik & bediening",
			questions: [
				{
					id: "gestures",
					title: "Hoe bedien ik de Earbuds met tikgebaren?",
					blocks: [
						{ t: "p", text: "De Earbuds hebben twee manieren van bediening: op de knop drukken (klikken) en tikken." },
						{
							t: "table",
							head: ["Gebaar", "In rust of tijdens afspelen", "Tijdens (inkomend) telefoongesprek"],
							rows: [
								["Kort op de knop van de linker of rechter Earbud drukken", "Audio pauzeren/afspelen", "Gesprek aannemen / Gesprek beëindigen"],
								["Twee keer kort op de knop van de linker Earbud drukken", "Naar de vorige dempingsmodus", "Gesprek weigeren"],
								["Twee keer kort op de knop van de rechter Earbud drukken", "Naar de volgende dempingsmodus", "Gesprek weigeren"],
								["Lang op de knop van de linker Earbud drukken", "Volume omlaag", "Volume omlaag"],
								["Lang op de knop van de rechter Earbud drukken", "Volume omhoog", "Volume omhoog"],
								["Twee keer tikken op de linker Earbud", "Vorig nummer", "Microfoon dempen"],
								["Twee keer tikken op de rechter Earbud", "Volgend nummer", "Microfoon dempen"],
							],
						},
						{ t: "img", src: img("tap-controls"), alt: "Waar je op de Earbuds drukt en tikt" },
					],
				},
				{
					id: "button-config",
					title: "Kan ik de acties van de knopbedieningen wijzigen?",
					blocks: [
						{ t: "p", text: "Je kunt in de myProtect-app wijzigen welke actie aan elk gebaar is toegewezen." },
						{
							t: "ol",
							items: [
								"Verbind je Earbuds met je apparaat en met de app. Locatieservices moeten zijn ingeschakeld om dit goed te laten werken.",
								"Ga naar Instellingen > Button Configuration.",
								"Je kunt nu de actie die aan elke knopbediening is toegewezen, wijzigen.",
							],
						},
					],
				},
				{
					id: "wear",
					title: "Hoe draag ik EarsOnly Protect op de juiste manier?",
					blocks: [
						{ t: "p", text: "Voor de beste pasvorm plaats je de Earbuds als volgt in je oor." },
						{
							t: "ol",
							items: [
								"Bepaal welke Earbud links en welke rechts is. De Earbud met de rode markering hoort in je rechteroor, de Earbud met de blauwe markering in je linkeroor.",
								"Duw het uiteinde met de kleurmarkering in het gehoorkanaal en draai deze iets naar achteren, zodat hij goed op zijn plek valt.",
								"Trek zo nodig de bovenkant van je oor een beetje omhoog, zodat de Earbud makkelijker in het oor glijdt.",
							],
						},
						{ t: "img", src: img("wear-earbuds"), alt: "De Earbud plaatsen: duwen en iets naar achteren draaien" },
					],
				},
			],
		},
		{
			id: "charging",
			title: "Opladen & batterij",
			questions: [
				{
					id: "charge",
					title: "Hoe laad ik mijn Earbuds en Oplaadcase op?",
					blocks: [
						{ t: "h", text: "De Oplaadcase" },
						{ t: "p", text: "Je kunt de case op een van de volgende manieren opladen:" },
						{
							t: "ul",
							items: [
								"**USB-C:** sluit de Oplaadcase met de meegeleverde USB-C-kabel aan op een USB-lader of USB-poort.",
								"**Draadloos opladen:** leg de Oplaadcase op een geschikte draadloze lader.",
							],
						},
						{ t: "h", text: "De Earbuds" },
						{ t: "p", text: "Leg de linker en rechter Earbud op de bijbehorende oplaadcontacten in de Oplaadcase, zoals weergegeven in de afbeelding." },
						{ t: "note", kind: "warning", text: "Zorg dat de oplaadcontacten van de Earbuds en de Oplaadcase schoon en vrij van vuil zijn. Als de Earbuds geen goed contact maken, laden ze niet op." },
						{ t: "p", text: "Zie {{q:clean}} voor instructies om de oplaadcontacten schoon te maken." },
						{ t: "img", src: img("charging"), alt: "Earbuds op de oplaadcontacten in de Oplaadcase" },
					],
				},
				{
					id: "battery-check",
					title: "Hoe controleer ik de batterijstatus van mijn Earbuds en Oplaadcase?",
					blocks: [
						{
							t: "ol",
							items: [
								"Open de Oplaadcase.",
								"Plaats beide Earbuds in de Oplaadcase.",
								"De leds op beide Earbuds geven automatisch elk hun eigen batterijniveau aan.",
								"De led op de Oplaadcase geeft het batterijniveau van de Oplaadcase aan.",
							],
						},
						{ t: "note", kind: "note", text: "Je kunt het batterijniveau van de Earbuds en de Oplaadcase ook controleren door kort op hun eigen knop te drukken." },
					],
				},
				{
					id: "leds",
					title: "Wat betekenen de leds op de Earbuds en de Oplaadcase?",
					blocks: [
						{ t: "p", text: "De leds op de Earbuds en de Oplaadcase geven het huidige batterijniveau of de oplaadstatus aan." },
						{ t: "img", src: img("led-status"), alt: "Led-kleuren voor het batterijniveau: groen 80% of meer, oranje 10 tot 80%, knipperend oranje onder 10%" },
						{ t: "h", text: "Batterijniveau" },
						{
							t: "ul",
							items: [
								"Is de led op de Earbud of de Oplaadcase **groen**, dan is de batterij voor 80% of meer opgeladen.",
								"Is de led op de Earbud of de Oplaadcase **oranje**, dan is de batterij voor 10 tot 80% opgeladen.",
								"Knippert de led **oranje**, dan is het batterijniveau lager dan 10%.",
							],
						},
						{ t: "h", text: "Oplaadstatus" },
						{
							t: "ul",
							items: [
								"Wanneer de case op een (draadloze) lader is aangesloten, tonen de leds op de case een oplaadanimatie waarbij de leds van links naar rechts springen.",
								"Als je de deksel van de Oplaadcase sluit, gaat het opladen door, maar gaat de animatie na korte tijd uit. Je kunt de oplaadstatus controleren door de deksel van de Oplaadcase weer te openen.",
								"Als de case volledig is opgeladen, worden alle drie de leds op de Oplaadcase groen en gaan ze daarna uit. Als je de deksel van de case opent, wordt de led weer groen.",
							],
						},
					],
				},
				{
					id: "not-charged",
					title: "Waarom zijn mijn Earbuds na lang opladen niet volledig opgeladen?",
					blocks: [
						{
							t: "ol",
							items: [
								"Zorg dat de Earbuds goed in de Oplaadcase liggen.",
								"Zorg dat de Oplaadcase meer dan 10% batterij heeft (zie: {{q:battery-check}})",
								"Maak de oplaadcontacten van de Earbuds en de Oplaadcase schoon met een licht vochtige doek. Wacht tot alles helemaal droog is voordat je de Earbuds terug in de Oplaadcase legt.",
								"Werkt dit niet? Reset dan de Earbuds en de Oplaadcase (zie: {{q:reset-earbuds}} en {{q:reset-case}}) en probeer het opnieuw.",
							],
						},
					],
				},
			],
		},
		{
			id: "audio",
			title: "Geluid & verbinding",
			questions: [
				{
					id: "volume-limit",
					title: "Waarom beperkt mijn telefoon het volume van mijn EarsOnly Protect?",
					blocks: [
						{ t: "p", text: "EarsOnly Protect beperkt het geluid van media tot een veilig luisterniveau om je gehoor gezond te houden. Als op je apparaat ook een volumebegrenzing aanstaat, kan die het maximale volume van je Earbuds onnodig beperken. Zet de volumebegrenzing van je apparaat uit als dat kan." },
					],
				},
				{
					id: "no-sound",
					title: "Waarom hoor ik (bijna) geen geluid uit de Earbud(s)?",
					blocks: [
						{ t: "h", text: "Zijn de Earbuds verbonden?" },
						{
							t: "ol",
							items: [
								"Controleer in het bluetooth-menu van je apparaat (onder instellingen) of de Earbuds verbonden zijn met de audiobron.",
								"Controleer of er audio wordt afgespeeld.",
								"Leg de Earbuds terug in de Oplaadcase, wacht 10 seconden en haal ze er weer uit.",
								"Is het afspelen gestopt? Start de audio dan opnieuw.",
							],
						},
						{ t: "h", text: "Controleer het geluidskanaal" },
						{
							t: "ol",
							start: 5,
							items: [
								"Controleer of de geluidskanalen van beide Earbuds nog open zijn en vrij van vuil of oorsmeer.",
								"Zijn de geluidskanalen (gedeeltelijk) verstopt? Maak ze dan schoon met de meegeleverde cerumenpen. (Zie: {{q:clean}})",
							],
						},
						{ t: "h", text: "Nog steeds geen geluid?" },
						{ t: "ol", start: 7, items: ["Reset de Earbuds en probeer het opnieuw (zie: {{q:reset-earbuds}})."] },
					],
				},
				{
					id: "out-of-sync",
					title: "Waarom loopt het geluid niet synchroon?",
					blocks: [
						{
							t: "ol",
							items: [
								"Leg de Earbuds in de Oplaadcase, wacht 10 seconden en haal ze er weer uit.",
								"Werkt dit niet? Reset dan de Earbuds en probeer het opnieuw (zie: {{q:reset-earbuds}}).",
							],
						},
					],
				},
			],
		},
		{
			id: "reset",
			title: "Resetten",
			questions: [
				{
					id: "reset-earbuds",
					title: "Hoe reset ik de Earbuds?",
					blocks: [
						{
							t: "ol",
							items: [
								"Haal beide Earbuds uit de Oplaadcase.",
								"Doe de Earbuds in je oren.",
								"Houd de knoppen van beide Earbuds tegelijk 15 seconden ingedrukt.",
								"Je hoort een opstartgeluid wanneer de Earbuds gereset zijn.",
							],
						},
						{ t: "note", kind: "note", text: "Je voorkeursinstellingen gaan niet verloren." },
					],
				},
				{
					id: "reset-case",
					title: "Hoe reset ik de Oplaadcase?",
					blocks: [
						{
							t: "ol",
							items: [
								"Houd de knop van de Oplaadcase 15 seconden ingedrukt.",
								"De led van de Oplaadcase wordt groen als de reset gelukt is.",
							],
						},
						{ t: "note", kind: "note", text: "Je voorkeursinstellingen gaan niet verloren." },
					],
				},
				{
					id: "factory-reset",
					title: "Hoe voer ik een fabrieksreset uit?",
					blocks: [
						{ t: "note", kind: "warning", text: "Bij een fabrieksreset verlies je je voorkeursinstellingen en de koppelgegevens met je apparaten." },
						{
							t: "ol",
							items: [
								"Zorg dat de Oplaadcase meer dan 10% is opgeladen.",
								"Zorg dat de Earbuds goed in de Oplaadcase liggen.",
								"Houd de knop op de Oplaadcase minstens 3 seconden ingedrukt. Wacht tot de leds op de Oplaadcase en de Earbuds blauw worden. Dit kan tot 5 seconden duren.",
								"Druk snel twee keer op de knop van de Oplaadcase. De leds van de Earbuds worden nu paars.",
								"Als de leds weer blauw worden, is de fabrieksreset gelukt.",
								"Verwijder de oude koppeling uit het bluetooth-menu van je apparaat.",
								"Je kunt je EarsOnly Protect nu opnieuw aan je apparaat koppelen.",
							],
						},
					],
				},
			],
		},
		{
			id: "maintenance",
			title: "Onderhoud",
			questions: [
				{
					id: "clean",
					title: "Hoe maak ik mijn EarsOnly Protect schoon?",
					blocks: [
						{ t: "note", kind: "warning", text: "Gebruik geen agressieve schoonmaakmiddelen of oplosmiddelen, zoals benzeen, thinner, afwasmiddel of alcohol, omdat die het oppervlak kunnen beschadigen. Neem contact op met de fabrikant voor meer informatie." },
						{ t: "h", text: "Dagelijks onderhoud" },
						{
							t: "ul",
							items: [
								"Veeg de buitenkant van de Earbuds af met een licht vochtige doek. Zorg dat de oplaadcontacten vrij van vuil zijn.",
								"Veeg de binnen- en buitenkant van de Oplaadcase af met een licht vochtige doek. Zorg dat de oplaadcontacten vrij van vuil zijn.",
							],
						},
						{ t: "note", kind: "important", text: "Zorg dat de Earbuds en de Oplaadcase helemaal droog zijn voordat je de Earbuds terug in de Oplaadcase legt." },
						{ t: "h", text: "Wekelijkse reiniging" },
						{
							t: "ul",
							items: [
								"Controleer of de geluidskanalen van de Earbuds open en vrij van vuil zijn.",
								"Zo niet, gebruik dan de meegeleverde cerumenpen om vuil uit het geluidskanaal te verwijderen.",
							],
						},
						{ t: "note", kind: "note", text: "Oorsmeer in het geluidskanaal van de Earbuds kan de geluidskwaliteit en het volume nadelig beïnvloeden." },
						{ t: "note", kind: "note", text: "Bovenstaande schoonmaakinstructies zijn gebaseerd op het advies van de fabrikant: maak schoon wanneer nodig." },
						{ t: "img", src: img("cleaning"), alt: "Het geluidskanaal van de Earbud schoonmaken met de cerumenpen" },
					],
				},
			],
		},
	],
};
